import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Menu } from "lucide-react";

import { moduleNavIcon } from "@/components/moduleNavIcons";
import type { HubMockupDocument } from "@/lib/hubMockupApi";
import { apiFetch } from "@/lib/api";
import { moduleNavBarItems, type NavItem } from "@/lib/appNavigation";
import { isFloorTabletDevice } from "@/lib/floorTabletDevice";
import { useIntegratorMe, type MeResponse } from "@/lib/me-query";
import { cn } from "@/lib/utils";

export type ModuleNavGroup = {
  id: string;
  label: string;
  icon: string;
  pagePaths: string[];
};

const EMPTY_GROUPS: ModuleNavGroup[] = [];
const MODULE_NAV_STORAGE_KEY = "hub:module-nav-groups:v1";

function readStoredModuleNavBundle(): Record<string, ModuleNavGroup[]> | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(MODULE_NAV_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, ModuleNavGroup[]>)
      : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredModuleNavBundle(bundle: Record<string, ModuleNavGroup[]>): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(MODULE_NAV_STORAGE_KEY, JSON.stringify(bundle));
  } catch {
    // ignore storage quota errors
  }
}

export function moduleNavQueryKey(): readonly ["module-nav"] {
  return ["module-nav"];
}

export async function fetchModuleNavBundle(): Promise<Record<string, ModuleNavGroup[]>> {
  const body = await apiFetch<{ groupsByModule: Record<string, ModuleNavGroup[]> }>("/api/module-nav");
  const bundle = body.groupsByModule ?? {};
  writeStoredModuleNavBundle(bundle);
  return bundle;
}

export function writeModuleNavGroupsForModule(
  queryClient: QueryClient,
  moduleKey: string,
  groups: ModuleNavGroup[],
): void {
  const current =
    queryClient.getQueryData<Record<string, ModuleNavGroup[]>>(moduleNavQueryKey()) ??
    readStoredModuleNavBundle() ??
    {};
  const next = { ...current, [moduleKey]: groups };
  queryClient.setQueryData(moduleNavQueryKey(), next);
  writeStoredModuleNavBundle(next);
  queryClient.setQueriesData<MeResponse>({ queryKey: ["me"] }, (old) =>
    old
      ? {
          ...old,
          moduleNavGroupsByModule: {
            ...(old.moduleNavGroupsByModule ?? {}),
            [moduleKey]: groups,
          },
        }
      : old,
  );
}

export function useModuleNavGroups(moduleKey: string): UseQueryResult<ModuleNavGroup[]> {
  const me = useIntegratorMe();
  const meBundle = me.data?.moduleNavGroupsByModule;
  useEffect(() => {
    if (meBundle) {
      writeStoredModuleNavBundle(meBundle);
    }
  }, [meBundle]);
  const initialBundle = meBundle ?? readStoredModuleNavBundle();
  return useQuery({
    queryKey: moduleNavQueryKey(),
    queryFn: fetchModuleNavBundle,
    initialData: initialBundle,
    initialDataUpdatedAt: meBundle ? me.dataUpdatedAt || Date.now() : undefined,
    enabled: meBundle === undefined,
    staleTime: 5 * 60_000,
    select: (bundle) => bundle?.[moduleKey] ?? EMPTY_GROUPS,
  });
}

function isSettingsItem(item: NavItem): boolean {
  return item.to.endsWith("/settings") || item.label.trim().toLowerCase() === "settings";
}

function groupForPath(groups: ModuleNavGroup[], pathname: string): ModuleNavGroup | null {
  let match: ModuleNavGroup | null = null;
  for (const group of groups) {
    if (group.pagePaths.some((pagePath) => pathname === pagePath || pathname.startsWith(`${pagePath}/`))) {
      match = group;
    }
  }
  return match;
}

function resolveVisibleGroups(
  groups: ModuleNavGroup[],
  navItems: NavItem[],
  pathname: string,
): { pages: NavItem[]; visibleGroups: ModuleNavGroup[]; current: ModuleNavGroup | null; currentPages: NavItem[] } {
  const pages = moduleNavBarItems(navItems).filter((item) => !isSettingsItem(item));
  const assigned = new Set(groups.flatMap((group) => group.pagePaths));
  const ungrouped = pages.filter((item) => !assigned.has(item.to));
  const visibleGroups: ModuleNavGroup[] = [
    ...groups,
    ...(ungrouped.length > 0
      ? [{ id: "ungrouped", label: "Ungrouped", icon: "boxes", pagePaths: ungrouped.map((item) => item.to) }]
      : []),
  ];
  const current = groupForPath(visibleGroups, pathname);
  const currentPages = current ? pages.filter((item) => current.pagePaths.includes(item.to)) : [];
  return { pages, visibleGroups, current, currentPages };
}

export function ModuleGroupedNav({
  app,
  navItems,
  pathname,
  hubNavDocument: _hubNavDocument,
  onNavigate,
}: {
  app: string;
  navItems: NavItem[];
  pathname: string;
  hubNavDocument: HubMockupDocument | null | undefined;
  onNavigate: () => void;
}) {
  const groupsQuery = useModuleNavGroups(app);
  const groups = groupsQuery.data ?? EMPTY_GROUPS;
  const [tablet, setTablet] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    const read = (): void => setTablet(isFloorTabletDevice());
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);

  if (!groupsQuery.isSuccess && !groupsQuery.isFetched) {
    return null;
  }

  const { pages, visibleGroups, current } = resolveVisibleGroups(groups, navItems, pathname);
  if (visibleGroups.length === 0) {
    return null;
  }

  if (!tablet) {
    return (
      <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" aria-label="Groups">
        {visibleGroups.map((group) => {
          const Icon = moduleNavIcon(group.icon);
          const active = current?.id === group.id;
          return (
            <Link
              key={group.id}
              to={pages.find((item) => group.pagePaths.includes(item.to))?.to ?? pathname}
              onClick={onNavigate}
              className={cn(
                "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium",
                active ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100",
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {group.label}
            </Link>
          );
        })}
      </nav>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center">
      <button
        type="button"
        aria-label="Open groups"
        onClick={() => setDrawerOpen(true)}
        className="inline-flex h-12 w-12 items-center justify-center rounded-md border border-slate-300 bg-white"
      >
        <Menu className="h-5 w-5" />
      </button>
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 flex">
          <button type="button" aria-label="Close groups" className="absolute inset-0 bg-slate-900/40" onClick={() => setDrawerOpen(false)} />
          <div className="relative flex h-full w-72 flex-col bg-white p-3 shadow-xl">
            {visibleGroups.map((group) => {
              const Icon = moduleNavIcon(group.icon);
              const active = group.id === current?.id;
              return (
                <Link
                  key={group.id}
                  to={pages.find((item) => group.pagePaths.includes(item.to))?.to ?? pathname}
                  onClick={() => {
                    setDrawerOpen(false);
                    onNavigate();
                  }}
                  className={cn(
                    "mb-1 flex min-h-12 w-full items-center gap-3 rounded-lg px-3 text-left text-base",
                    active ? "bg-brand-50 font-semibold text-brand-800" : "text-slate-800 hover:bg-slate-100",
                  )}
                >
                  <Icon className="h-5 w-5 text-slate-500" aria-hidden />
                  <span className="flex-1">{group.label}</span>
                  <span className="text-xs text-slate-400">{group.pagePaths.length}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ModuleGroupedSubnav({
  app,
  navItems,
  pathname,
  shellWidth,
  onNavigate,
}: {
  app: string;
  navItems: NavItem[];
  pathname: string;
  shellWidth: string;
  onNavigate: () => void;
}) {
  const groupsQuery = useModuleNavGroups(app);
  const groups = groupsQuery.data ?? EMPTY_GROUPS;
  const [tablet, setTablet] = useState(false);
  useEffect(() => {
    const read = (): void => setTablet(isFloorTabletDevice());
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);

  if (!groupsQuery.isSuccess && !groupsQuery.isFetched) {
    return null;
  }

  const { current, currentPages } = resolveVisibleGroups(groups, navItems, pathname);
  if (!current) {
    return null;
  }

  if (!tablet) {
    if (currentPages.length === 0) {
      return null;
    }
    return (
      <nav
        className={cn("mx-auto flex w-full gap-1 overflow-x-auto border-t border-slate-100", shellWidth)}
        aria-label={`${current.label} pages`}
      >
        {currentPages.map((item) => {
          const selected = pathname === item.to || pathname.startsWith(`${item.to}/`);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={cn(
                "inline-flex h-10 shrink-0 items-center border-b-2 px-3 text-sm font-medium",
                selected ? "border-brand-600 text-brand-700" : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>
    );
  }

  if (currentPages.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn("mx-auto flex w-full gap-2 overflow-x-auto pb-3", shellWidth)}
      aria-label={`${current.label} pages`}
    >
      {currentPages.map((item) => {
        const selected = pathname === item.to || pathname.startsWith(`${item.to}/`);
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={cn(
              "inline-flex h-12 shrink-0 items-center rounded-full border px-4 text-base font-medium",
              selected ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white text-slate-700",
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
